import { BoxProps } from "@shopify/restyle";
import React, { useMemo, useState } from "react";

import { CustomColor } from "@whoosh/utilities/src/colors";

import { Theme } from "../../../theme/restyle/theme";
import { RestyleColorString } from "../../../theme/types";
import { DepartmentIcon, DepartmentType } from "../../icons_v2/department";
import { Box } from "../../primitive";

const ICON_SIZE = 16;
const EXTRA_ICONS = 4;

export type EventPlaceholderImageProps = {
  eventColor: CustomColor;
  width: BoxProps<Theme>["width"];
  height: BoxProps<Theme>["height"];
  borderRadius: BoxProps<Theme>["borderRadius"];
  departmentType: DepartmentType;
};

export const EventPlaceholderImage = ({
  eventColor,
  width,
  height,
  borderRadius,
  departmentType,
}: EventPlaceholderImageProps) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const iconColorString: RestyleColorString = `surface-user-selectable-color${
    eventColor ?? "A"
  }-subtle`;

  const bgColorString: RestyleColorString = `surface-user-selectable-color${
    eventColor ?? "A"
  }-subtlest`;

  const departmentIconComponent = useMemo(
    () => (
      <DepartmentIcon
        type={departmentType}
        iconProps={{ color: iconColorString, testID: "department-icon" }}
      />
    ),
    [departmentType, iconColorString]
  );

  // Calculate number of icons needed
  const numIcons = useMemo(() => {
    if (!dimensions.width || !dimensions.height) return 25; // Default fallback

    // Calculate how many icons we need horizontally
    const iconsPerRow = Math.ceil(dimensions.width / ICON_SIZE) + EXTRA_ICONS;
    // Calculate how many rows we need
    const numRows = Math.ceil(dimensions.height / ICON_SIZE) + EXTRA_ICONS;

    return iconsPerRow * numRows;
  }, [dimensions]);

  return (
    <Box
      testID="event-placeholder-image"
      bg={bgColorString}
      borderRadius={borderRadius}
      width={width}
      height={height}
      overflow="hidden"
      onLayout={(event) => {
        const { width: layoutWidth, height: layoutHeight } =
          event.nativeEvent.layout;
        setDimensions({ width: layoutWidth, height: layoutHeight });
      }}
    >
      <Box flex={1} flexDirection="row" flexWrap="wrap" style={{ margin: -1 }}>
        {Array.from({ length: numIcons }).map((_, index) => (
          <Box
            key={index}
            flex={1}
            minWidth={16}
            minHeight={16}
            alignItems="center"
            justifyContent="center"
            m={1}
          >
            {departmentIconComponent}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
